import React, { useEffect, useState } from 'react';
import { Checkbox, TextField } from '@mui/material';
import { gtmService } from '../../../../utils/gtm.service';
import { StepperButtons } from '../../brand/components/StepperButtons';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IRegisterInfluencer } from '../dto/IRegisterInfluencer';
import { AuthService } from '../../../service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../utils/toasters';
import { useNavigate } from 'react-router-dom';
import { InternalRoutes } from '../../../../enum/InternalRoutes';
import { useRecaptcha } from '../../../../shared/hooks/useRecaptcha';
import { PasswordVerifySteps } from '../../../../shared/PasswordVerifySteps';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import InputPassword from '../../../../shared/components/input/InputPassword';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';

export function InfluencerStep1({ stepIdNumber, handleBackStep }: IRegisterStepperProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setToken, executeRecaptcha } = useRecaptcha();

    const { agencyCompany } = useSelector((state: IAllStates) => state.influencerRegistration);

    const [isLoading, setIsLoading] = useState(false);
    const [registerInfluencer, setRegisterInfluencer] = useState<IRegisterInfluencer>({
        displayName: '',
        email: '',
        isGeneralTermsAccepted: false,
        password: '',
        repeatPassword: '',
    });

    useEffect(() => {
        setRegisterInfluencer(prevState => ({
            ...prevState,
            agencyCompany: agencyCompany?.agency,
            email: agencyCompany?.email ?? '',
        }));
    }, [agencyCompany]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>, isCheckbox = false) => {
        const { name, value, checked } = event.target;
        setRegisterInfluencer(prev => ({
            ...prev,
            [name]: isCheckbox ? checked : value,
        }));
    };

    const handleNextStep = async () => {
        if (!executeRecaptcha) {
            return;
        }
        const result = await executeRecaptcha('register');
        setToken(result);
        if (registerInfluencer?.isGeneralTermsAccepted) {
            setIsLoading(true);
            AuthService.registerInfluencer({
                ...registerInfluencer,
            })
                .then(response => {
                    if (response) {
                        gtmService('influencer_lead');
                        navigate(
                            `${InternalRoutes.INFLUENCER_REGISTRATION}/${stepIdNumber + 1}?token=${
                                response?.verificationUUID
                            }`
                        );
                        SuccessToast(t('auth.register.brand.step1.pleaseCheckEmail'));
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => setIsLoading(false));
        } else {
            WarningToast(t('auth.register.brand.step1.termsNotSelected'));
        }
    };

    return (
        <Form
            onSubmit={e => {
                e.preventDefault();
                handleNextStep();
            }}
        >
            <Row>
                <Col md={6}>
                    <TextField
                        label={t('auth.register.influencer.step1.creator_name')}
                        variant="outlined"
                        size="small"
                        required
                        name="displayName"
                        onChange={changeHandler}
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <TextField
                        label="E-mail"
                        variant="outlined"
                        size="small"
                        required
                        type="email"
                        value={registerInfluencer.email}
                        name="email"
                        disabled={!!agencyCompany?.email}
                        onChange={changeHandler}
                        className="w-100 my-2"
                    />
                </Col>
                <Col md={6}>
                    <InputPassword
                        label={t('auth.register.influencer.step1.password')}
                        name="password"
                        className="w-100 my-2"
                        onChange={changeHandler}
                        required
                    />
                    <PasswordVerifySteps password={registerInfluencer?.password} />
                </Col>
                <Col md={6}>
                    <InputPassword
                        label={t('auth.register.influencer.step1.repeatPassword')}
                        name="repeatPassword"
                        className="w-100 my-2"
                        onChange={changeHandler}
                        required
                    />
                </Col>
                <div className="col-12 my-2 d-flex align-items-center">
                    <Checkbox
                        defaultChecked={false}
                        className="px-0"
                        onChange={e => {
                            changeHandler(e, true);
                        }}
                        size={'small'}
                        id="checkbox"
                        name="isGeneralTermsAccepted"
                        color="primary"
                    />
                    <label
                        htmlFor="checkbox"
                        className="ml-2 mb-0 text-start cursor-pointer"
                        dangerouslySetInnerHTML={{ __html: t('auth.register.influencer.step1.terms') }}
                    />
                </div>
                <div className="col-12 mt-4">
                    <StepperButtons
                        currentStep={stepIdNumber - 1}
                        handleNextStep={handleNextStep}
                        isLoading={isLoading}
                        handleBackStep={handleBackStep}
                    />
                </div>
            </Row>
        </Form>
    );
}
